import React, { useEffect, useMemo, useState } from 'react';
import { providersAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, UserRound, Wallet, Eye, CheckCircle2 } from 'lucide-react';

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  specialty: '',
  notes: '',
  is_active: true,
  provider_type: 'external',
  nif: '',
  iban: '',
};

const paymentFormInitial = {
  provider_id: '',
  provider_name: '',
  provider_specialty: '',
  sale_id: '',
  sale_number: '',
  sale_item_id: '',
  service_name: '',
  customer_name: '',
  service_date: '',
  amount_due: 0,
  amount_paid: 0,
  payment_date: '',
  payment_method: '',
  notes: '',
  status: 'paid',
};

const money = (v) => `€${Number(v || 0).toFixed(2)}`;
const paymentMethods = [
  ['bank_transfer', 'Transferência bancária'],
  ['cash', 'Dinheiro'],
  ['mbway', 'MB Way'],
  ['card', 'Cartão / Multibanco'],
  ['check', 'Cheque'],
  ['direct_debit', 'Débito direto'],
  ['other', 'Outro'],
];
const paymentMethodLabel = (value) => paymentMethods.find(([key]) => key === value)?.[1] || value || '-';
const rowKey = (row) => `${row.sale_id}::${row.sale_item_id}`;

export default function ProvidersPage() {
  const [providers, setProviders] = useState([]);
  const [summaryRows, setSummaryRows] = useState([]);
  const [summaryTotals, setSummaryTotals] = useState(null);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [showStatementModal, setShowStatementModal] = useState(false);
  const [statementLoading, setStatementLoading] = useState(false);
  const [statementData, setStatementData] = useState(null);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState(paymentFormInitial);
  const [selectedServices, setSelectedServices] = useState([]);
  const [paymentItems, setPaymentItems] = useState([]);

  const loadData = async () => {
    try {
      setLoading(true);

      const [providersRes, financialRes] = await Promise.all([
        providersAPI.getAll(),
        providersAPI.getFinancialSummary().catch(() => ({ data: { summary: null, rows: [] } })),
      ]);

      setProviders(Array.isArray(providersRes?.data) ? providersRes.data : []);
      setSummaryRows(Array.isArray(financialRes?.data?.rows) ? financialRes.data.rows : []);
      setSummaryTotals(financialRes?.data?.summary || null);
    } catch (error) {
      console.error('Erro ao carregar prestadores:', error);
      toast.error('Erro ao carregar prestadores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const financialMap = useMemo(() => {
    const m = new Map();
    for (const row of summaryRows) {
      m.set(String(row.provider_id || ''), row);
    }
    return m;
  }, [summaryRows]);

  const openCreate = () => {
    setEditingProvider(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (provider) => {
    setEditingProvider(provider);
    setForm({
      name: provider?.name || '',
      email: provider?.email || '',
      phone: provider?.phone || '',
      specialty: provider?.specialty || '',
      notes: provider?.notes || '',
      is_active: provider?.is_active ?? true,
      provider_type: provider?.provider_type || 'external',
      nif: provider?.nif || '',
      iban: provider?.iban || '',
    });
    setShowModal(true);
  };

  const saveProvider = async () => {
    if (!String(form.name || '').trim()) {
      toast.error('Indica o nome do prestador');
      return;
    }

    try {
      if (editingProvider?.id) {
        await providersAPI.update(editingProvider.id, form);
        toast.success('Prestador atualizado com sucesso');
      } else {
        await providersAPI.create(form);
        toast.success('Prestador criado com sucesso');
      }

      setShowModal(false);
      setEditingProvider(null);
      setForm(emptyForm);
      await loadData();
    } catch (error) {
      console.error('Erro ao guardar prestador:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao guardar prestador');
    }
  };

  const removeProvider = async (provider) => {
    const ok = window.confirm(`Apagar o prestador "${provider?.name || ''}"?`);
    if (!ok) return;

    try {
      await providersAPI.remove(provider.id);
      toast.success('Prestador apagado com sucesso');
      await loadData();
    } catch (error) {
      console.error('Erro ao apagar prestador:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao apagar prestador');
    }
  };

  const openStatement = async (provider) => {
    try {
      setShowStatementModal(true);
      setStatementLoading(true);
      setStatementData(null);
      setSelectedServices([]);

      const res = await providersAPI.getStatement(provider.id);
      setStatementData(res?.data || null);
    } catch (error) {
      console.error('Erro ao carregar extrato do prestador:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao carregar extrato do prestador');
      setShowStatementModal(false);
    } finally {
      setStatementLoading(false);
    }
  };

  const openPaymentForRows = (rows) => {
    if (!rows.length) return;
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const total = rows.reduce((sum, row) => sum + Number(row.amount_due || 0), 0);
    const first = rows[0];

    setPaymentForm({
      ...paymentFormInitial,
      provider_id: first.provider_id || '',
      provider_name: first.provider_name || '',
      provider_specialty: first.provider_specialty || '',
      amount_due: total,
      amount_paid: total,
      payment_date: today,
      payment_method: 'bank_transfer',
      status: 'paid',
    });
    setPaymentItems(rows);
    setShowPaymentModal(true);
  };

  const openMarkAsPaid = (row) => openPaymentForRows([row]);

  const toggleService = (row) => {
    const key = rowKey(row);
    setSelectedServices((current) => current.includes(key) ? current.filter((item) => item !== key) : [...current, key]);
  };

  const openSelectedPayments = () => {
    const selected = (statementData?.items || []).filter((row) => row.status !== 'paid' && selectedServices.includes(rowKey(row)));
    openPaymentForRows(selected);
  };

  const submitPayment = async () => {
    if (!paymentForm.payment_date) {
      toast.error('Indica a data de pagamento');
      return;
    }

    if (!paymentForm.payment_method) {
      toast.error('Seleciona a modalidade de pagamento');
      return;
    }
    if (!paymentItems.length) {
      toast.error('Seleciona pelo menos um serviço');
      return;
    }

    try {
      setSavingPayment(true);
      await providersAPI.createPaymentsBatch({
        provider_id: paymentForm.provider_id,
        items: paymentItems.map((row) => ({ sale_id: row.sale_id, sale_item_id: row.sale_item_id })),
        payment_date: paymentForm.payment_date,
        payment_method: paymentForm.payment_method,
        notes: paymentForm.notes,
      });

      toast.success(`${paymentItems.length} pagamento${paymentItems.length === 1 ? '' : 's'} registado${paymentItems.length === 1 ? '' : 's'} com sucesso`);
      setShowPaymentModal(false);
      setSelectedServices([]);
      setPaymentItems([]);

      if (statementData?.provider?.id) {
        const res = await providersAPI.getStatement(statementData.provider.id);
        setStatementData(res?.data || null);
      }

      await loadData();
    } catch (error) {
      console.error('Erro ao registar pagamento:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao registar pagamento');
    } finally {
      setSavingPayment(false);
    }
  };

  const providersSorted = [...providers].sort((a, b) =>
    String(a?.name || '').localeCompare(String(b?.name || ''), 'pt')
  );

  return (
    <div className="p-4 md:p-6 space-y-4 !text-slate-900 opacity-100" style={{ opacity: 1, filter: "none" }}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold !text-white">Prestadores</h1>
          <p className="text-sm !text-slate-200">Gestão básica, extrato e pagamentos internos</p>
        </div>

        <button onClick={openCreate} className="inline-flex items-center rounded-xl bg-emerald-600 px-4 py-2 font-medium text-white hover:bg-emerald-700">
          <Plus className="w-4 h-4 mr-2" />
          Novo Prestador
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" style={{ opacity: 1 }}>
        <div className="rounded-2xl border border-slate-300 !bg-white p-4 shadow-sm opacity-100">
          <div className="text-sm font-medium text-slate-600">Total a pagar</div>
          <div className="mt-1 text-2xl font-bold text-slate-900">{money(summaryTotals?.total_due)}</div>
        </div>

        <div className="rounded-2xl border border-slate-300 !bg-white p-4 shadow-sm opacity-100">
          <div className="text-sm font-medium text-slate-600">Total pago</div>
          <div className="mt-1 text-2xl font-bold text-emerald-700">{money(summaryTotals?.total_paid)}</div>
        </div>

        <div className="rounded-2xl border border-slate-300 !bg-white p-4 shadow-sm opacity-100">
          <div className="text-sm font-medium text-slate-600">Em aberto</div>
          <div className="mt-1 text-2xl font-bold text-rose-700">{money(summaryTotals?.total_open)}</div>
        </div>
      </div>

      <div className="!bg-white border border-slate-300 rounded-2xl shadow-sm overflow-hidden opacity-100">
        <div className="overflow-x-auto">
          <table className="w-full text-sm !text-slate-900 opacity-100">
            <thead className="!bg-slate-100">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold text-slate-800" style={{ color: "#1e293b", opacity: 1 }}>Nome</th>
                <th className="px-4 py-3 font-semibold text-slate-800" style={{ color: "#1e293b", opacity: 1 }}>Especialidade</th>
                <th className="px-4 py-3 font-semibold text-slate-800" style={{ color: "#1e293b", opacity: 1 }}>Tipo</th>
                <th className="px-4 py-3 font-semibold text-slate-800" style={{ color: "#1e293b", opacity: 1 }}>Em aberto</th>
                <th className="px-4 py-3 font-semibold text-slate-800" style={{ color: "#1e293b", opacity: 1 }}>Pago</th>
                <th className="px-4 py-3 font-semibold text-slate-800" style={{ color: "#1e293b", opacity: 1 }}>Serviços</th>
                <th className="px-4 py-3 font-semibold text-slate-800" style={{ color: "#1e293b", opacity: 1 }}>Estado</th>
                <th className="px-4 py-3 font-semibold text-slate-800 text-right" style={{ color: "#1e293b", opacity: 1 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-slate-700">
                    A carregar...
                  </td>
                </tr>
              ) : providersSorted.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-10 text-center text-slate-700">
                    <div className="flex flex-col items-center gap-2">
                      <UserRound className="w-8 h-8 opacity-40" />
                      <span>Sem prestadores registados</span>
                    </div>
                  </td>
                </tr>
              ) : (
                providersSorted.map((provider) => {
                  const fin = financialMap.get(String(provider.id || '')) || null;
                  const totalOpen = Number(fin?.total_open || 0);
                  const totalPaid = Number(fin?.total_paid || 0);
                  const servicesCount = Number(fin?.services_count || 0);

                  return (
                    <tr key={provider.id} className="border-t border-slate-200" style={{ opacity: 1, color: "#0f172a" }}>
                      <td className="px-4 py-3 text-slate-900 font-medium" style={{ color: "#0f172a", opacity: 1 }}>{provider.name}</td>
                      <td className="px-4 py-3 text-slate-700" style={{ color: "#334155", opacity: 1 }}>{provider.specialty || '-'}</td>
                      <td className="px-4 py-3 text-slate-700" style={{ color: "#334155", opacity: 1 }}>
                        {provider.provider_type === 'internal' ? 'Interno' : 'Externo'}
                      </td>
                      <td className="px-4 py-3 font-semibold text-rose-700" style={{ color: "#be123c", opacity: 1 }}>{money(totalOpen)}</td>
                      <td className="px-4 py-3 font-semibold text-emerald-700" style={{ color: "#047857", opacity: 1 }}>{money(totalPaid)}</td>
                      <td className="px-4 py-3 text-slate-700" style={{ color: "#334155", opacity: 1 }}>{servicesCount}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                          provider.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'
                        }`}>
                          {provider.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => openStatement(provider)}
                            className="border-slate-300"
                            title="Ver extrato"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => openEdit(provider)}
                            className="border-slate-300"
                            title="Editar"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => removeProvider(provider)}
                            className="border-rose-300 text-rose-600 hover:bg-rose-50"
                            title="Apagar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="modal-light w-[95vw] max-w-xl p-5 bg-white border border-slate-300 text-slate-900">
          <DialogHeader>
            <DialogTitle className="!text-slate-900 font-bold">
              {editingProvider ? 'Editar Prestador' : 'Novo Prestador'}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="Nome" className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-emerald-500" />
            <input value={form.specialty} onChange={(e) => setForm((prev) => ({ ...prev, specialty: e.target.value }))} placeholder="Especialidade" className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-emerald-500" />
            <input value={form.phone} onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))} placeholder="Telefone" className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-emerald-500" />
            <input value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} placeholder="Email" className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-emerald-500" />
            <input value={form.nif} onChange={(e) => setForm((prev) => ({ ...prev, nif: e.target.value }))} placeholder="NIF" className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-emerald-500" />
            <input value={form.iban} onChange={(e) => setForm((prev) => ({ ...prev, iban: e.target.value }))} placeholder="IBAN" className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>

          <div className="grid grid-cols-2 gap-3 mt-3">
            <Button
              type="button"
              variant={form.provider_type === 'external' ? 'default' : 'outline'}
              onClick={() => setForm((prev) => ({ ...prev, provider_type: 'external' }))}
              className={form.provider_type === 'external' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
            >
              Externo
            </Button>
            <Button
              type="button"
              variant={form.provider_type === 'internal' ? 'default' : 'outline'}
              onClick={() => setForm((prev) => ({ ...prev, provider_type: 'internal' }))}
              className={form.provider_type === 'internal' ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}
            >
              Interno
            </Button>
          </div>

          <label className="flex items-center gap-2 mt-3 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={!!form.is_active}
              onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
            />
            Prestador ativo
          </label>

          <input value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} placeholder="Notas" className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-emerald-500" />

          <DialogFooter>
            <button type="button" onClick={() => setShowModal(false)} className="rounded-xl border border-slate-400 bg-white px-4 py-2 text-slate-900 hover:bg-slate-50">
              Cancelar
            </button>
            <button type="button" onClick={saveProvider} className="rounded-xl bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700">
              Guardar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showStatementModal} onOpenChange={setShowStatementModal}>
        <DialogContent className="modal-light w-[96vw] max-w-6xl max-h-[90vh] overflow-y-auto p-5 bg-white border border-slate-300 text-slate-900">
          <DialogHeader>
            <DialogTitle className="!text-slate-900 flex items-center gap-2 font-bold">
              <Wallet className="w-5 h-5" />
              Extrato do Prestador
            </DialogTitle>
          </DialogHeader>

          {statementLoading ? (
            <div className="py-8 text-center text-slate-500">A carregar extrato...</div>
          ) : !statementData ? (
            <div className="py-8 text-center text-slate-500">Sem dados.</div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-4" style={{ backgroundColor: "#ffffff", color: "#0f172a", opacity: 1 }}>
                <div className="text-xl font-bold text-slate-900">{statementData?.provider?.name || '-'}</div>
                <div className="text-sm text-slate-700">{statementData?.provider?.specialty || '-'}</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" style={{ opacity: 1 }}>
                <div className="rounded-2xl border border-slate-200 bg-white p-4" style={{ backgroundColor: "#ffffff", color: "#0f172a", opacity: 1 }}>
                  <div className="text-sm font-medium text-slate-600">Total a pagar</div>
                  <div className="mt-1 text-2xl font-bold text-slate-900">{money(statementData?.summary?.total_due)}</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4" style={{ backgroundColor: "#ffffff", color: "#0f172a", opacity: 1 }}>
                  <div className="text-sm font-medium text-slate-600">Total pago</div>
                  <div className="mt-1 text-2xl font-bold text-emerald-700">{money(statementData?.summary?.total_paid)}</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4" style={{ backgroundColor: "#ffffff", color: "#0f172a", opacity: 1 }}>
                  <div className="text-sm font-medium text-slate-600">Em aberto</div>
                  <div className="mt-1 text-2xl font-bold text-rose-700">{money(statementData?.summary?.total_open)}</div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" style={{ backgroundColor: "#ffffff", color: "#0f172a", opacity: 1 }}>
                <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
                  <span className="font-semibold text-slate-900">Serviços</span>
                  <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={(statementData?.items || []).some((row) => row.status !== 'paid') && (statementData?.items || []).filter((row) => row.status !== 'paid').every((row) => selectedServices.includes(rowKey(row)))}
                        onChange={(e) => setSelectedServices(e.target.checked ? (statementData?.items || []).filter((row) => row.status !== 'paid').map(rowKey) : [])}
                      />
                      Selecionar todos em aberto
                    </label>
                    <button
                      type="button"
                      onClick={openSelectedPayments}
                      disabled={!selectedServices.length}
                      className="rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Registar pagamento ({selectedServices.length})
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm !text-slate-900 opacity-100">
                    <thead className="!bg-slate-100">
                      <tr className="text-left">
                        <th className="w-10 px-4 py-3" aria-label="Selecionar" />
                        <th className="px-4 py-3 font-semibold text-slate-700" style={{ color: "#334155", opacity: 1 }}>Data</th>
                        <th className="px-4 py-3 font-semibold text-slate-700" style={{ color: "#334155", opacity: 1 }}>Serviço</th>
                        <th className="px-4 py-3 font-semibold text-slate-700" style={{ color: "#334155", opacity: 1 }}>Cliente</th>
                        <th className="px-4 py-3 font-semibold text-slate-700" style={{ color: "#334155", opacity: 1 }}>Venda</th>
                        <th className="px-4 py-3 font-semibold text-slate-700" style={{ color: "#334155", opacity: 1 }}>A pagar</th>
                        <th className="px-4 py-3 font-semibold text-slate-800" style={{ color: "#1e293b", opacity: 1 }}>Estado</th>
                        <th className="px-4 py-3 font-semibold text-slate-700 text-right" style={{ color: "#334155", opacity: 1 }}>Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(statementData?.items || []).length === 0 ? (
                        <tr>
                          <td colSpan="8" className="px-4 py-8 text-center text-slate-700">Sem serviços registados para este prestador.</td>
                        </tr>
                      ) : (
                        (statementData?.items || []).map((row) => (
                          <tr key={`${row.sale_id}-${row.sale_item_id}`} className="border-t border-slate-200" style={{ opacity: 1, color: "#0f172a" }}>
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                disabled={row.status === 'paid'}
                                checked={row.status !== 'paid' && selectedServices.includes(rowKey(row))}
                                onChange={() => toggleService(row)}
                                aria-label={`Selecionar ${row.service_name || 'serviço'}`}
                              />
                            </td>
                            <td className="px-4 py-3 text-slate-700" style={{ color: "#334155", opacity: 1 }}>{String(row.service_date || '').slice(0, 10) || '-'}</td>
                            <td className="px-4 py-3 text-slate-900 font-medium" style={{ color: "#0f172a", opacity: 1 }}>{row.service_name || '-'}</td>
                            <td className="px-4 py-3 text-slate-700" style={{ color: "#334155", opacity: 1 }}>{row.customer_name || '-'}</td>
                            <td className="px-4 py-3 text-slate-700" style={{ color: "#334155", opacity: 1 }}>{row.sale_number || '-'}</td>
                            <td className="px-4 py-3 font-semibold text-slate-900" style={{ color: "#0f172a", opacity: 1 }}>{money(row.amount_due)}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                                row.status === 'paid'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {row.status === 'paid' ? 'Pago' : 'Em aberto'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              {row.status === 'paid' ? (
                                <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                                  <CheckCircle2 className="w-4 h-4" />
                                  Pago
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => openMarkAsPaid(row)}
                                  className="rounded-xl bg-emerald-600 px-3 py-2 text-white hover:bg-emerald-700"
                                >
                                  Marcar pago
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" style={{ backgroundColor: "#ffffff", color: "#0f172a", opacity: 1 }}>
                <div className="px-4 py-3 border-b border-slate-200 font-semibold text-slate-900">Pagamentos registados</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm !text-slate-900 opacity-100">
                    <thead className="!bg-slate-100">
                      <tr className="text-left">
                        <th className="px-4 py-3 font-semibold text-slate-700" style={{ color: "#334155", opacity: 1 }}>Data</th>
                        <th className="px-4 py-3 font-semibold text-slate-700" style={{ color: "#334155", opacity: 1 }}>Serviço</th>
                        <th className="px-4 py-3 font-semibold text-slate-700" style={{ color: "#334155", opacity: 1 }}>Venda</th>
                        <th className="px-4 py-3 font-semibold text-slate-700">Método</th>
                        <th className="px-4 py-3 font-semibold text-slate-700">Valor</th>
                        <th className="px-4 py-3 font-semibold text-slate-700">Notas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(statementData?.payments || []).length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-4 py-8 text-center text-slate-700">Sem pagamentos registados.</td>
                        </tr>
                      ) : (
                        (statementData?.payments || []).map((row) => (
                          <tr key={row.id} className="border-t border-slate-200" style={{ opacity: 1, color: "#0f172a" }}>
                            <td className="px-4 py-3 text-slate-700" style={{ color: "#334155", opacity: 1 }}>{row.payment_date || '-'}</td>
                            <td className="px-4 py-3 text-slate-900 font-medium" style={{ color: "#0f172a", opacity: 1 }}>{row.service_name || '-'}</td>
                            <td className="px-4 py-3 text-slate-700" style={{ color: "#334155", opacity: 1 }}>{row.sale_number || '-'}</td>
                            <td className="px-4 py-3 text-slate-700" style={{ color: "#334155", opacity: 1 }}>{paymentMethodLabel(row.payment_method)}</td>
                            <td className="px-4 py-3 font-semibold text-emerald-700" style={{ color: "#047857", opacity: 1 }}>{money(row.amount_paid)}</td>
                            <td className="px-4 py-3 text-slate-700" style={{ color: "#334155", opacity: 1 }}>{row.notes || '-'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="modal-light w-[95vw] max-w-xl p-5 !bg-white !text-slate-900 border border-slate-300 opacity-100" style={{ background: "#ffffff", color: "#0f172a", opacity: 1 }}>
          <DialogHeader>
            <DialogTitle className="!text-slate-900 font-bold">Registar pagamento ao prestador</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-xl border border-slate-200 !bg-white p-4 opacity-100">
              <div className="text-sm text-slate-700">Prestador</div>
              <div className="font-bold text-slate-900">{paymentForm.provider_name || '-'}</div>
              <div className="text-sm text-slate-500 mt-2">{paymentItems.length} serviço{paymentItems.length === 1 ? '' : 's'} selecionado{paymentItems.length === 1 ? '' : 's'}</div>
              <div className="mt-2 max-h-44 space-y-2 overflow-y-auto">
                {paymentItems.map((row) => (
                  <div key={rowKey(row)} className="flex justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                    <div>
                      <div className="font-medium text-slate-900">{row.service_name || '-'}</div>
                      <div className="text-slate-500">{row.sale_number || '-'}</div>
                    </div>
                    <div className="font-semibold text-slate-900">{money(row.amount_due)}</div>
                  </div>
                ))}
              </div>
            </div>

            <input
              type="date"
              value={paymentForm.payment_date}
              onChange={(e) => setPaymentForm((prev) => ({ ...prev, payment_date: e.target.value }))}
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500"
            />

            <select
              value={paymentForm.payment_method}
              onChange={(e) => setPaymentForm((prev) => ({ ...prev, payment_method: e.target.value }))}
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Selecionar modalidade de pagamento</option>
              {paymentMethods.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>

            <div className="rounded-xl border border-slate-200 !bg-slate-50 p-3 text-sm !text-slate-800 opacity-100">
              Total a pagar: <span className="font-bold text-slate-900">{money(paymentForm.amount_due)}</span>
            </div>

            <input
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm((prev) => ({ ...prev, notes: e.target.value }))}
              placeholder="Notas internas"
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowPaymentModal(false)} className="border-slate-400 text-slate-900 bg-white hover:bg-slate-50">
              Cancelar
            </Button>
            <button
              type="button"
              onClick={submitPayment}
              disabled={savingPayment}
              className="rounded-xl bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {savingPayment ? 'A guardar...' : 'Guardar pagamento'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
